import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Avatar,
  AvatarGroup,
  IconButton,
  Button,
  Chip,
  Slider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import {
  MoreVert,
  PlayArrow,
  Pause,
  ContentCopy,
  OpenInNew,
  GraphicEq,
  CheckCircle,
  CloudDone,
  RecordVoiceOver,
  Sync,
  Download,
  PersonAdd,
  Close
} from '@mui/icons-material';
import { Meeting } from '../../types';
import {
  fetchTranscriptContent,
  parseTranscript,
  TranscriptCue,
  DEFAULT_SAMPLE_TRANSCRIPT,
  shareMeetingApi
} from '../../services/meetingService';

interface MeetingDetailsProps {
  meeting: Meeting | null;
  onPlay?: () => void;
  onTabChange?: () => void;
  autoPlay?: boolean;
  onParticipantAdded?: (meetingId: string, email: string) => void;
}

const MeetingDetails: React.FC<MeetingDetailsProps> = ({
  meeting,
  onPlay,
  onTabChange,
  autoPlay,
  onParticipantAdded
}) => {
  const [tab, setTab] = useState(0); // Default to 0: Transcript
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [transcriptText, setTranscriptText] = useState<string>(DEFAULT_SAMPLE_TRANSCRIPT);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [cues, setCues] = useState<TranscriptCue[]>(() => parseTranscript(DEFAULT_SAMPLE_TRANSCRIPT));
  const [copied, setCopied] = useState(false);

  // Share Dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareSuccess, setShareSuccess] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const cueRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);

  // When meeting changes, reset player and load transcript
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    if (!meeting) {
      setTranscriptText(DEFAULT_SAMPLE_TRANSCRIPT);
      setCues(parseTranscript(DEFAULT_SAMPLE_TRANSCRIPT));
      return;
    }

    const initialText = meeting.rawTranscript || DEFAULT_SAMPLE_TRANSCRIPT;
    setTranscriptText(initialText);
    setCues(parseTranscript(initialText));

    if (meeting.transcriptUrl) {
      setTranscriptLoading(true);
      fetchTranscriptContent(meeting.transcriptUrl)
        .then((text) => {
          if (text) {
            setTranscriptText(text);
            setCues(parseTranscript(text));
          }
        })
        .finally(() => {
          setTranscriptLoading(false);
        });
    }
  }, [meeting]);

  // Handle autoPlay when requested from outside
  useEffect(() => {
    if (autoPlay && meeting) {
      setIsPlaying(true);
      if (audioRef.current) {
        audioRef.current
          .play()
          .then(() => setIsPlaying(true))
          .catch((err) => {
            console.warn('Auto-play fallback:', err);
            setIsPlaying(true);
          });
      }
    }
  }, [autoPlay, meeting]);

  // Compute active cue index based on currentTime
  const activeCueIndex = cues.findIndex((cue, i) => {
    const nextCue = cues[i + 1];
    const end = nextCue ? nextCue.startTime : cue.startTime + 6;
    return currentTime >= cue.startTime && currentTime < end;
  });

  // Auto-focus and scroll to the active transcript cue during audio playback
  useEffect(() => {
    if (activeCueIndex >= 0 && cueRefs.current[activeCueIndex]) {
      const activeEl = cueRefs.current[activeCueIndex];
      if (activeEl && transcriptContainerRef.current) {
        activeEl.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [activeCueIndex]);

  const togglePlay = () => {
    onPlay?.(); // Collapse meeting list whenever play audio is clicked!

    if (!audioRef.current) {
      setIsPlaying(!isPlaying);
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.warn('Audio playback simulation:', err);
          setIsPlaying(true);
        });
    }
  };

  // Simulation timer if audio file is not playable
  useEffect(() => {
    let interval: any;
    if (isPlaying && (!audioRef.current || !meeting?.audioUrl)) {
      interval = setInterval(() => {
        setCurrentTime((prev) => {
          if (prev >= 225) {
            setIsPlaying(false);
            return 0;
          }
          return prev + 0.5;
        });
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isPlaying, meeting?.audioUrl]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current && !isNaN(audioRef.current.duration)) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (_: Event, newValue: number | number[]) => {
    const target = Array.isArray(newValue) ? newValue[0] : newValue;
    setCurrentTime(target);
    if (audioRef.current) {
      audioRef.current.currentTime = target;
    }
  };

  const handleCueClick = (cue: TranscriptCue) => {
    onPlay?.(); // Collapse meeting list when clicking a transcript cue!
    setCurrentTime(cue.startTime);
    if (audioRef.current) {
      audioRef.current.currentTime = cue.startTime;
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(true));
    } else {
      setIsPlaying(true);
    }
  };

  const togglePlaybackRate = () => {
    const rates = [1, 1.25, 1.5, 2];
    const nextIdx = (rates.indexOf(playbackRate) + 1) % rates.length;
    const nextRate = rates[nextIdx];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || seconds <= 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCopyTranscript = () => {
    const textToCopy = transcriptText || meeting?.rawTranscript || DEFAULT_SAMPLE_TRANSCRIPT;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadTranscript = () => {
    const textToDownload = transcriptText || meeting?.rawTranscript || DEFAULT_SAMPLE_TRANSCRIPT;
    const blob = new Blob([textToDownload], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeTitle = (meeting?.title || 'meeting').toLowerCase().replace(/[^a-z0-9]+/g, '_');
    link.download = `${safeTitle}_transcript.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadAudio = async () => {
    if (!meeting?.audioUrl) return;
    try {
      const res = await fetch(meeting.audioUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = (meeting.title || 'recording').toLowerCase().replace(/[^a-z0-9]+/g, '_');
      link.download = `${safeTitle}_audio.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      // Fallback direct link download
      const link = document.createElement('a');
      link.href = meeting.audioUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      const safeTitle = (meeting.title || 'recording').toLowerCase().replace(/[^a-z0-9]+/g, '_');
      link.download = `${safeTitle}_audio.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleShareSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!meeting) return;

    const email = shareEmail.trim();
    if (!email || !email.includes('@')) {
      setShareError('Please enter a valid email address.');
      return;
    }

    setIsSharing(true);
    setShareError(null);
    setShareSuccess(null);

    try {
      const meetingId = meeting.meeting_id || meeting.id;
      await shareMeetingApi(meetingId, email);

      // Notify parent to update participants state data immediately
      onParticipantAdded?.(meeting.id, email);

      setShareSuccess(`Successfully shared with ${email}`);
      setShareEmail('');
      setTimeout(() => {
        setShareDialogOpen(false);
        setShareSuccess(null);
      }, 1400);
    } catch (err: any) {
      setShareError(err.message || 'Failed to share meeting. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  // If no meeting selected, show clean placeholder
  if (!meeting) {
    return (
      <Box className="neumorphic-card" sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center' }}>
        <RecordVoiceOver sx={{ fontSize: 48, color: 'var(--hp-text-secondary)', mb: 1.5, opacity: 0.7 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
          No Meeting Selected
        </Typography>
        <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ mt: 0.5, maxWidth: 280, fontSize: '0.85rem' }}>
          Select a meeting from the list to view its live audio recording and synchronized transcript.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="neumorphic-card" sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.2, overflowY: 'hidden', minHeight: 0 }}>
      {/* Real HTML5 audio element */}
      {meeting.audioUrl && (
        <audio
          ref={audioRef}
          src={meeting.audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleAudioEnded}
          preload="metadata"
        />
      )}

      {/* Selected Meeting Header (Moved up) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
        <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.05rem', color: 'var(--hp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {meeting.title}
          </Typography>
          <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ display: 'block', mt: 0.2 }}>
            {meeting.date} • {meeting.time}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0 }}>
          {/* Participants AvatarGroup */}
          <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 26, height: 26, fontSize: '0.75rem' } }}>
            {meeting.participants.map((p) => (
              <Tooltip key={p.id} title={`${p.name} (${p.email || 'Participant'})`}>
                <Avatar alt={p.name} src={p.avatar}>
                  {p.name.charAt(0)}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>

          {/* Share Meeting Button */}
          <Tooltip title="Share meeting with teammate">
            <IconButton
              size="small"
              onClick={() => {
                setShareDialogOpen(true);
                setShareError(null);
                setShareSuccess(null);
              }}
              sx={{
                color: 'var(--hp-primary)',
                bgcolor: 'var(--hp-primary-light)',
                p: 0.5,
                borderRadius: '8px',
                '&:hover': { bgcolor: '#d4e1ff' }
              }}
            >
              <PersonAdd sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          <IconButton size="small"><MoreVert fontSize="small" /></IconButton>
        </Box>
      </Box>

      {/* Cloud Run Status Badges & Quick Audio Download Chip */}
      <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        {meeting.audioUrl ? (
          <Tooltip title="Click to download audio (.mp4)">
            <Chip
              icon={<Download sx={{ fontSize: 13 }} />}
              label="Download Audio"
              size="small"
              onClick={handleDownloadAudio}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: 'rgba(34, 201, 154, 0.15)',
                color: '#168A69',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(34, 201, 154, 0.25)' }
              }}
            />
          </Tooltip>
        ) : (
          <Chip
            icon={<Sync sx={{ fontSize: 14 }} />}
            label="Interactive Audio"
            size="small"
            sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(79, 107, 255, 0.12)', color: 'var(--hp-primary)', fontWeight: 600 }}
          />
        )}
        <Chip
          icon={<CloudDone sx={{ fontSize: 14 }} />}
          label="Live Transcript"
          size="small"
          sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(79, 107, 255, 0.15)', color: 'var(--hp-primary)', fontWeight: 600 }}
        />
        <Chip
          icon={<PersonAdd sx={{ fontSize: 13 }} />}
          label={`Share (${meeting.participants.length})`}
          size="small"
          onClick={() => {
            setShareDialogOpen(true);
            setShareError(null);
            setShareSuccess(null);
          }}
          sx={{
            height: 22,
            fontSize: '0.7rem',
            bgcolor: 'var(--hp-surface-raised)',
            border: '1px solid var(--hp-border)',
            color: 'var(--hp-text)',
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)' }
          }}
        />
      </Box>

      {/* Synchronized Audio Player Bar */}
      <Box sx={{ bgcolor: 'var(--hp-bg)', borderRadius: '12px', p: 1, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, border: '1px solid var(--hp-border)' }}>
        <IconButton
          onClick={togglePlay}
          size="small"
          sx={{
            color: 'var(--hp-primary)',
            bgcolor: 'var(--hp-primary-light)',
            '&:hover': { bgcolor: '#d0daff' },
            width: 32,
            height: 32
          }}
        >
          {isPlaying ? <Pause sx={{ fontSize: 18 }} /> : <PlayArrow sx={{ fontSize: 18 }} />}
        </IconButton>

        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 0.5 }}>
          <Slider
            size="small"
            value={currentTime}
            min={0}
            max={duration > 0 ? duration : 224}
            onChange={handleSeek}
            sx={{
              color: 'var(--hp-primary)',
              py: 0.5,
              '& .MuiSlider-thumb': {
                width: 10,
                height: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }
            }}
          />
        </Box>

        <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ minWidth: 68, textAlign: 'right', fontSize: '0.72rem', fontWeight: 600 }}>
          {formatTime(currentTime)} / {duration > 0 ? formatTime(duration) : '03:44'}
        </Typography>

        <Tooltip title="Playback Speed">
          <Typography
            variant="caption"
            onClick={togglePlaybackRate}
            sx={{
              cursor: 'pointer',
              fontWeight: 700,
              color: 'var(--hp-primary)',
              bgcolor: 'var(--hp-primary-light)',
              px: 0.6,
              py: 0.3,
              borderRadius: '6px',
              fontSize: '0.7rem'
            }}
          >
            {playbackRate}x
          </Typography>
        </Tooltip>

        {meeting.audioUrl && (
          <Tooltip title="Download audio recording (.mp4)">
            <Button
              size="small"
              startIcon={<Download sx={{ fontSize: 14 }} />}
              onClick={handleDownloadAudio}
              sx={{
                textTransform: 'none',
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'var(--hp-primary)',
                bgcolor: 'var(--hp-primary-light)',
                py: 0.3,
                px: 0.8,
                borderRadius: '6px',
                flexShrink: 0,
                minWidth: 'auto',
                '&:hover': { bgcolor: '#d2deff' }
              }}
            >
              Audio
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Navigation Tabs (Collapse happens when tabs are clicked) */}
      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v);
          onTabChange?.(); // Collapse meeting list when a tab is clicked!
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: '30px',
          flexShrink: 0,
          '& .MuiTab-root': {
            minHeight: '30px',
            textTransform: 'none',
            fontSize: '0.8rem',
            color: 'var(--hp-text-secondary)',
            py: 0,
            px: 1.4,
            minWidth: 'auto'
          },
          '& .Mui-selected': {
            color: 'var(--hp-primary)',
            bgcolor: 'var(--hp-primary-light)',
            borderRadius: '10px',
            fontWeight: 700
          },
          '& .MuiTabs-indicator': { display: 'none' }
        }}
      >
        <Tab label="Transcript" />
        <Tab label="Summary" />
        <Tab label="Actions" />
      </Tabs>

      {/* TAB 1: Timestamped Transcript (Positioned at bottom with auto-scroll focus) */}
      {tab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Transcript Toolbar with Download & Copy */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.84rem' }}>
                Transcript
              </Typography>
              {isPlaying && (
                <Chip
                  size="small"
                  icon={<GraphicEq sx={{ fontSize: 12, color: 'var(--hp-success) !important' }} />}
                  label="Syncing..."
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(34, 201, 154, 0.12)', color: 'var(--hp-success)', fontWeight: 600 }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              {/* Option to download transcript */}
              <Tooltip title="Download transcript as .txt file">
                <Button
                  startIcon={<Download sx={{ fontSize: 13 }} />}
                  size="small"
                  onClick={handleDownloadTranscript}
                  sx={{
                    textTransform: 'none',
                    bgcolor: 'var(--hp-primary-light)',
                    color: 'var(--hp-primary)',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    py: 0.2,
                    px: 0.8,
                    borderRadius: '6px',
                    '&:hover': { bgcolor: '#d2deff' }
                  }}
                >
                  Download
                </Button>
              </Tooltip>

              <Button
                startIcon={<ContentCopy sx={{ fontSize: 13 }} />}
                size="small"
                onClick={handleCopyTranscript}
                sx={{ textTransform: 'none', color: 'var(--hp-text-secondary)', fontSize: '0.72rem', py: 0.2, px: 0.8 }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>

              {meeting.transcriptUrl && (
                <IconButton
                  size="small"
                  href={meeting.transcriptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in Storage"
                  sx={{ color: 'var(--hp-primary)', p: 0.3 }}
                >
                  <OpenInNew sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Transcript Scrollable Box Focused by Timestamp */}
          {transcriptLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 1 }}>
              <CircularProgress size={22} sx={{ color: 'var(--hp-primary)' }} />
              <Typography variant="caption" color="var(--hp-text-secondary)">
                Loading transcript...
              </Typography>
            </Box>
          ) : (
            <Box
              ref={transcriptContainerRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.8,
                pr: 0.5,
                scrollBehavior: 'smooth'
              }}
            >
              {cues.map((cue, index) => {
                const isActive = activeCueIndex === index;

                return (
                  <Box
                    key={cue.id}
                    ref={(el: HTMLDivElement | null) => {
                      cueRefs.current[index] = el;
                    }}
                    onClick={() => handleCueClick(cue)}
                    sx={{
                      p: 1,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      bgcolor: isActive ? 'var(--hp-primary-light)' : 'var(--hp-surface)',
                      borderLeft: isActive ? '4px solid var(--hp-primary)' : '3px solid transparent',
                      boxShadow: isActive ? '0 2px 8px rgba(79, 107, 255, 0.18)' : 'none',
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        bgcolor: isActive ? 'var(--hp-primary-light)' : 'var(--hp-surface-raised)',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        {isActive && (
                          <GraphicEq sx={{ fontSize: 13, color: 'var(--hp-primary)' }} />
                        )}
                        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: isActive ? 'var(--hp-primary)' : 'var(--hp-text)' }}>
                          {cue.speaker || 'Speaker'}
                        </Typography>
                      </Box>
                      <Chip
                        label={formatTime(cue.startTime)}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: isActive ? 'var(--hp-primary)' : 'var(--hp-bg)',
                          color: isActive ? '#FFFFFF' : 'var(--hp-text-secondary)',
                          cursor: 'pointer'
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.82rem',
                        lineHeight: 1.45,
                        color: isActive ? 'var(--hp-text)' : 'var(--hp-text-secondary)',
                        fontWeight: isActive ? 600 : 400
                      }}
                    >
                      {cue.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* TAB 2: Summary */}
      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, overflowY: 'auto', pr: 0.5, flex: 1, minHeight: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.86rem' }}>AI Meeting Summary</Typography>
            <Button
              startIcon={<ContentCopy sx={{ fontSize: 13 }} />}
              size="small"
              onClick={handleCopyTranscript}
              sx={{ textTransform: 'none', color: 'var(--hp-text-secondary)', fontSize: '0.72rem', py: 0.2 }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </Box>

          <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ lineHeight: 1.5, fontSize: '0.82rem' }}>
            {meeting.summary}
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 'bold', fontSize: '0.86rem' }}>Key Takeaways</Typography>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--hp-text-secondary)', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {meeting.keyPoints?.map((point, i) => (
              <li key={i}>{point}</li>
            ))}
          </ul>

          {meeting.host_email && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'var(--hp-surface)', borderRadius: '8px', border: '1px solid var(--hp-border)' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--hp-text)', display: 'block' }}>
                Firestore Auth Verified
              </Typography>
              <Typography variant="caption" color="var(--hp-text-secondary)">
                Host: {meeting.host_email}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* TAB 3: Actions */}
      {tab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, overflowY: 'auto', pr: 0.5, flex: 1, minHeight: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.86rem' }}>Extracted Action Items</Typography>
          {[
            'Testing webhooks integration for automatic transcription ingestion',
            'Priyanka to use agent to fetch transcript from storage bucket and provide summaries',
            'Summaries update back to BigQuery table',
            'Dharni to run smoke testing tomorrow'
          ].map((action, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8, p: 0.8, bgcolor: 'var(--hp-bg)', borderRadius: '8px' }}>
              <CheckCircle sx={{ fontSize: 15, color: 'var(--hp-success)', mt: 0.2 }} />
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'var(--hp-text)' }}>{action}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Share Meeting Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => !isSharing && setShareDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: '16px',
              p: 1,
              bgcolor: 'var(--hp-surface)'
            }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--hp-text)' }}>
            Share Meeting
          </Typography>
          <IconButton size="small" onClick={() => setShareDialogOpen(false)} disabled={isSharing}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleShareSubmit}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ fontSize: '0.82rem' }}>
              Enter your teammate's email to share this meeting's audio recording, transcript, and AI summary.
            </Typography>

            {/* Current Participants Preview */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--hp-text-secondary)' }}>
                Current Participants ({meeting.participants.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {meeting.participants.map((p) => (
                  <Chip
                    key={p.id}
                    avatar={<Avatar alt={p.name} src={p.avatar} />}
                    label={p.name}
                    size="small"
                    sx={{ fontSize: '0.72rem', bgcolor: 'var(--hp-surface-raised)' }}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              autoFocus
              label="Teammate Email"
              type="email"
              placeholder="e.g. colleague@company.com"
              value={shareEmail}
              onChange={(e) => {
                setShareEmail(e.target.value);
                setShareError(null);
              }}
              fullWidth
              size="small"
              required
              disabled={isSharing}
              sx={{ mt: 0.5 }}
            />

            {shareError && (
              <Alert severity="error" sx={{ borderRadius: '8px', fontSize: '0.78rem' }}>
                {shareError}
              </Alert>
            )}

            {shareSuccess && (
              <Alert severity="success" sx={{ borderRadius: '8px', fontSize: '0.78rem' }}>
                {shareSuccess}
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setShareDialogOpen(false)}
              disabled={isSharing}
              sx={{ textTransform: 'none', color: 'var(--hp-text-secondary)' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSharing || !shareEmail.trim()}
              startIcon={isSharing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PersonAdd sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: 'var(--hp-primary)',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.84rem',
                '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
              }}
            >
              {isSharing ? 'Sharing...' : 'Share Meeting'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default MeetingDetails;
